// NEW: src/components/chapter-end-celebration.js
//
// Full-screen celebration shown when the student reaches the chapter-complete
// synthetic slide (last slide of the last concept). Replaces the earlier
// bullet-list chapter-complete render.
//
// Contract:
//   - Sky-blue gradient background (matches section-marker).
//   - Confetti burst fires on mount, fades at 2.5 s.
//   - Stats pills are defensive: any stat that's null/undefined is omitted
//     (no fabricated numbers — "no-fake-data" rule).
//   - Encouragement line: picked once per mount from a localised pool.
//   - Primary CTA (green) → onHome. Secondary CTA (ghost) → onReview.
//   - Capacitor haptic + sound ('chapter_complete') are fired by the PARENT
//     on mount — this component is pure render.
//
// This component does NOT render the shared bottom nav. The parent
// (EnhancedLearnMode) hides it for chapter-complete slides so the two
// inline CTAs here are the only way out.

import { useMemo } from "react";
import { getFontFamily } from "../constants/colors";
import ConfettiBurst from "./confetti-burst";

const HEADLINES = {
  en: "Chapter complete!",
  hi: "Chapter poora ho gaya!",
};

const ENCOURAGEMENTS = {
  en: [
    "Every concept, nailed. Come back tomorrow for spaced revision.",
    "You just went from zero to fluent. Take a bow.",
    "Chapter done. Your brain is 1% bigger today.",
  ],
  hi: [
    "Ek poora chapter! Bahut badhiya. Kal revision aayega.",
    "Shaabaash. Chapter khatam. Kal wapas aana.",
    "Chapter ho gaya. Kal revise karna — aur mazaa aayega.",
  ],
};

const LABELS = {
  en: {
    concepts: "concepts",
    questions: "questions",
    mins: "mins",
    backHome: "Back to home",
    review: "Review this chapter",
  },
  hi: {
    concepts: "concepts",
    questions: "sawaal",
    mins: "min",
    backHome: "Home par jaayein",
    review: "Phir se dekho",
  },
};

export function ChapterEndCelebration({
  chapterName,
  stats = {},       // { concepts, questions, mins } — any missing key is omitted
  lang = "en",
  onHome,
  onReview,
}) {
  const ff = getFontFamily(lang);
  const L = LABELS[lang] || LABELS.en;
  const pool = ENCOURAGEMENTS[lang] || ENCOURAGEMENTS.en;
  // Pick once per mount — not re-computed on every re-render
  const encouragement = useMemo(() => pool[Math.floor(Math.random() * pool.length)], [pool]);

  // Build stats pills only from non-null / non-undefined values. Zero is fine
  // (zero concepts is still a real number), but null/undefined means "don't show".
  const pills = [];
  if (stats.concepts != null) pills.push(`${stats.concepts} ${L.concepts}`);
  if (stats.questions != null) pills.push(`${stats.questions} ${L.questions}`);
  if (stats.mins != null) pills.push(`${stats.mins} ${L.mins}`);

  return (
    <div
      style={{
        position: "relative",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 32px",
        background: "linear-gradient(180deg, #6AAAC8 0%, #8BC4D8 100%)",
        fontFamily: ff,
        textAlign: "center",
        overflow: "hidden",
        animation: "samajh-cc-fade 300ms ease-out both",
      }}
    >
      {/* Confetti sits above the gradient, below the text */}
      <ConfettiBurst />

      {/* Content layer */}
      <div style={{ position: "relative", zIndex: 3, width: "100%", maxWidth: 420 }}>
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: "#FFFFFF",
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
            fontFamily: ff,
          }}
        >
          {HEADLINES[lang] || HEADLINES.en}
        </div>

        {chapterName && (
          <div
            style={{
              marginTop: 12,
              fontSize: 18,
              fontWeight: 500,
              color: "rgba(255,255,255,0.85)",
              lineHeight: 1.35,
              fontFamily: ff,
            }}
          >
            {chapterName}
          </div>
        )}

        {pills.length > 0 && (
          <div
            style={{
              margin: "32px 0",
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              justifyContent: "center",
            }}
          >
            {pills.map((text, i) => (
              <div
                key={i}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  color: "#FFFFFF",
                  padding: "8px 16px",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: ff,
                  whiteSpace: "nowrap",
                }}
              >
                {text}
              </div>
            ))}
          </div>
        )}

        {pills.length === 0 && (
          // Keep vertical rhythm similar even when no stats are available
          <div style={{ height: 32 }} />
        )}

        <div
          style={{
            fontSize: 16,
            color: "#FFFFFF",
            lineHeight: 1.5,
            fontWeight: 400,
            maxWidth: 300,
            margin: "0 auto",
            fontFamily: ff,
          }}
        >
          {encouragement}
        </div>

        <div style={{ marginTop: 32, width: "100%" }}>
          <button
            type="button"
            onClick={onHome}
            style={{
              width: "100%",
              background: "#1A7A52",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 12,
              padding: "14px 28px",
              fontSize: 16,
              fontWeight: 600,
              fontFamily: ff,
              cursor: "pointer",
              minHeight: 48,
              boxShadow: "0 4px 14px rgba(26,122,82,0.35)",
            }}
          >
            {L.backHome}
          </button>

          <button
            type="button"
            onClick={onReview}
            style={{
              marginTop: 12,
              width: "100%",
              background: "transparent",
              color: "#FFFFFF",
              border: "2px solid #FFFFFF",
              borderRadius: 12,
              padding: "14px 28px",
              fontSize: 16,
              fontWeight: 600,
              fontFamily: ff,
              cursor: "pointer",
              minHeight: 48,
            }}
          >
            {L.review}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes samajh-cc-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default ChapterEndCelebration;
